import { Input, type InputRef } from "antd";
import React, { useEffect, useRef, type ChangeEvent } from "react";
import styles from "./stepper-input.module.scss";
import { MdRemove, MdOutlineAdd } from "react-icons/md";
import classNames from "classnames";
import { NumericFormat } from "react-number-format";
import useDotsLoader from "@/hooks/useDotsLoader.hook";

type Props = {
  size?: "small" | "large";
  disabled?: boolean;
  fullDisabled?: boolean;
  value: number | string;
  incrementToken?: () => void;
  decrementToken?: () => void;
  changeTokenAmount: (amount: number | undefined) => void;
  handleMaxAmount?: () => void;
  incrementControls?: boolean;
  isFocused?: boolean;
  maxValue?: number;
  isLoading?: boolean;
  onInputClick?: (e: React.MouseEvent) => void;
};

const StepperInput = ({
  fullDisabled = false,
  size = "large",
  disabled = false,
  decrementToken,
  incrementToken,
  value,
  changeTokenAmount,
  handleMaxAmount,
  incrementControls = false,
  isFocused = false,
  maxValue,
  isLoading = false,
  onInputClick,
}: Props) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const dots = useDotsLoader(200);

  const handleMore = (e: React.MouseEvent) => {
    e.stopPropagation();
    incrementToken?.();
  };

  const handleLess = (e: React.MouseEvent) => {
    e.stopPropagation();
    decrementToken?.();
  };

  const handleMax = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleMaxAmount?.();
  };

  const handleClick = (e: React.MouseEvent) => {
    // Call custom click handler if provided (for multi-token cards)
    if (onInputClick) {
      onInputClick(e);
    }

    // Don't auto-select text - let user click to position cursor normally
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
      // Don't auto-select text - just focus for user input
    }
  }, [isFocused]);

  return (
    <div className={classNames(styles.inputArea, styles[size])}>
      <div className={styles.inputWrapper}>
        <NumericFormat
          customInput={Input}
          value={value}
          getInputRef={(ref: HTMLInputElement) => (inputRef.current = ref)}
          thousandSeparator=","
          decimalScale={4}
          allowLeadingZeros={false}
          isAllowed={(values) => {
            const { floatValue } = values;
            if (!floatValue || !maxValue) return true;
            return floatValue <= maxValue;
          }}
          placeholder={isLoading ? `Loading${dots}` : "Enter amount"}
          onValueChange={(e) => changeTokenAmount(e.floatValue)}
          className={classNames(
            styles.cardInput,
            disabled && styles.disabled,
            (value && Number(value) > 0) && styles.hasValue
          )}
          disabled={fullDisabled}
          onClick={handleClick}
        />
        <div className={styles.buttons}>
          {incrementControls && (
            <div className={styles.incrementControls}>
              <button
                className={styles.less}
                onClick={handleLess}
                disabled={disabled}
                aria-label="decrement-token">
                <MdRemove />
              </button>
              <button
                className={styles.more}
                onClick={handleMore}
                disabled={disabled}
                aria-label="increment-token">
                <MdOutlineAdd />
              </button>
              {!disabled && (
                <button onClick={handleMax} className={styles.maxBtn}>
                  Max
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepperInput;
